import { revertedTemplateEditToolCallIdsAtom } from '@/atoms/chatAtoms';
import { useRevertTemplateEdit } from '@/components/chat/hooks/useRevertTemplateEdit';
import { useTemplateRevertPreview } from '@/components/chat/hooks/useTemplateRevertPreview';
import { useViewTemplateById } from '@/hooks/useViewTemplateById';
import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyP2Bold,
  TypographyPBold,
} from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import { useInvokeOnce } from '@heidi/utils';
import { useAtom } from 'jotai';
import { AlertCircle, ChevronRight, FileText, Loader2, Undo2 } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TemplatePreviewThumbnail } from './TemplatePreviewThumbnail';

interface EditTemplateSectionBlockProps {
  status: 'applying' | 'completed' | 'error';
  templateId?: string;
  templateName?: string;
  error?: string;
  onBlockError: (blockType: 'edit_template_section', error?: string) => void;
  /** Stable tool-call id, used to persist the terminal "reverted" state. */
  toolId?: string;
  className?: string;
}

// Cap the persisted reverted-id map; prune oldest past this on write.
const MAX_REVERTED_TOOL_CALL_IDS = 200;

type RevertedMap = Record<string, number | true>;

const writeReverted = (
  ids: RevertedMap,
  toolId: string,
  value: number | true
): RevertedMap => {
  const next: RevertedMap = { ...ids };
  next[toolId] = value;
  const keys = Object.keys(next);
  if (keys.length > MAX_REVERTED_TOOL_CALL_IDS) {
    for (const stale of keys.slice(0, keys.length - MAX_REVERTED_TOOL_CALL_IDS)) {
      delete next[stale];
    }
  }
  return next;
};

const unlockReverted = (ids: RevertedMap, toolId: string): RevertedMap => {
  const next: RevertedMap = { ...ids };
  delete next[toolId];
  return next;
};

/**
 * Renders an Ask Heidi `edit_template_section` tool call. On success it offers
 * View and Revert. The reverted state is persisted by tool-call id so it
 * survives card remounts and can't be triggered twice.
 */
export const EditTemplateSectionBlock = ({
  status,
  templateId,
  templateName,
  error,
  onBlockError,
  toolId,
  className,
}: EditTemplateSectionBlockProps): JSX.Element => {
  const intl = useIntl();
  useInvokeOnce(
    () => onBlockError('edit_template_section', error),
    status === 'error'
  );
  const viewTemplate = useViewTemplateById();
  const { mutateAsync: revertTemplateEdit, isPending: isReverting } =
    useRevertTemplateEdit();
  const { data: revertPreview } = useTemplateRevertPreview(templateId);
  const previewVersionNumber = revertPreview?.previousVersionNumber ?? null;
  const [revertedToolCallIds, setRevertedToolCallIds] = useAtom(
    revertedTemplateEditToolCallIdsAtom
  );
  const revertedEntry = toolId ? revertedToolCallIds[toolId] : undefined;
  const isReverted = revertedEntry !== undefined;
  const revertedToVersion =
    typeof revertedEntry === 'number' ? revertedEntry : undefined;

  if (status === 'applying') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-xl border border-border bg-surface-accent p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="edit-template-section-block"
        data-status="applying"
      >
        <FileText
          className="size-5 shrink-0 text-text-tertiary"
          aria-hidden="true"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyPBold className="line-clamp-2 text-text-primary">
            {templateName ?? (
              <FormattedMessage defaultMessage="Template" id="3JxaKsUDfq" />
            )}
          </TypographyPBold>
          <div className="flex items-center gap-1.5">
            <Loader2 className="size-3 animate-spin text-text-tertiary" />
            <TypographyCaption className="text-text-tertiary">
              <FormattedMessage
                defaultMessage="Updating template..."
                id="YNA4nW27xW"
              />
            </TypographyCaption>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-xl border border-validation-destructive-border bg-validation-destructive-muted p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="edit-template-section-block"
        data-status="error"
      >
        <AlertCircle
          className="size-5 shrink-0 text-validation-destructive"
          aria-hidden="true"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyPBold className="line-clamp-2 text-text-primary">
            <FormattedMessage
              defaultMessage="Couldn't update template"
              id="oYO9U6uoZf"
            />
          </TypographyPBold>
          <TypographyCaption className="text-validation-destructive">
            {error ?? (
              <FormattedMessage
                defaultMessage="Something went wrong while updating the template."
                id="aqladBaMaR"
              />
            )}
          </TypographyCaption>
        </div>
      </div>
    );
  }

  const resolvedName =
    templateName ??
    intl.formatMessage({
      defaultMessage: 'Template',
      id: '3JxaKsUDfq',
    });

  if (isReverted) {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-8 overflow-hidden rounded-xl border border-border bg-surface-accent p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="edit-template-section-block"
        data-status="reverted"
      >
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <TypographyPBold className="line-clamp-2 text-text-disabled line-through">
            <FormattedMessage
              defaultMessage="{name} updated"
              id="nwdXRqLoIP"
              values={{ name: resolvedName }}
            />
          </TypographyPBold>
          <TypographyP2Bold className="text-text-secondary">
            {revertedToVersion !== undefined ? (
              <FormattedMessage
                defaultMessage="Reverted to v{version}"
                id="6evkmS0lWl"
                values={{ version: revertedToVersion }}
              />
            ) : (
              <FormattedMessage defaultMessage="Reverted" id="h6qopMZfNT" />
            )}
          </TypographyP2Bold>
        </div>
        <div className="relative w-[104px] shrink-0 self-stretch">
          <TemplatePreviewThumbnail
            variant="reverted"
            className="absolute start-0 top-0"
          />
        </div>
      </div>
    );
  }

  // Completed
  const canView = Boolean(templateId);
  // Need a toolId to persist the reverted state, so gate Revert on it.
  const canRevert = Boolean(templateId) && Boolean(toolId) && !isReverting;

  const handleView = () => {
    if (!templateId) return;
    void viewTemplate(templateId);
  };

  const handleRevert = () => {
    if (!templateId || !toolId) return;
    // Lock before awaiting so a mid-flight remount can't fire a second revert.
    setRevertedToolCallIds((prev) => writeReverted(prev, toolId, true));
    revertTemplateEdit(templateId)
      .then(({ toVersionNumber }) => {
        setRevertedToolCallIds((prev) =>
          writeReverted(prev, toolId, toVersionNumber)
        );
      })
      .catch(() => {
        // Roll back on failure (toast is shown by the hook) so the user can retry.
        setRevertedToolCallIds((prev) => unlockReverted(prev, toolId));
      });
  };

  return (
    <div
      className={cn(
        'flex w-full items-center gap-8 overflow-hidden rounded-xl border border-border bg-surface-accent p-4',
        'transition-all duration-200',
        className
      )}
      data-testid="edit-template-section-block"
      data-status="completed"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <TypographyPBold className="line-clamp-2 text-text-primary">
          <FormattedMessage
            defaultMessage="{name} updated"
            id="nwdXRqLoIP"
            values={{ name: resolvedName }}
          />
        </TypographyPBold>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            flush="all"
            className="text-text-secondary"
            onClick={handleView}
            disabled={!canView}
          >
            <FormattedMessage defaultMessage="View" id="FgydNeoT78" />
            <ChevronRight className="rtl:-scale-x-100" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            flush="all"
            className="text-text-secondary"
            onClick={handleRevert}
            disabled={!canRevert}
          >
            {previewVersionNumber !== null ? (
              <FormattedMessage
                defaultMessage="Revert to v{version}"
                id="8dQ7Q7F6o6"
                values={{ version: previewVersionNumber }}
              />
            ) : (
              <FormattedMessage defaultMessage="Revert" id="amT0GhE8ux" />
            )}
            <Undo2 className="rtl:-scale-x-100" />
          </Button>
        </div>
      </div>
      <div className="relative w-[104px] shrink-0 self-stretch">
        <TemplatePreviewThumbnail className="absolute start-0 top-0" />
      </div>
    </div>
  );
};
